/**
 * Fullscreen-on-gesture, shared by two consumers: the game-start Play button
 * (auto-fullscreen, todo/phone-fullscreen-hide-browser-chrome.md) and the in-game
 * fullscreen-button.js (re-entry after a navigation or back-gesture exit,
 * todo/fullscreen-on-resumed-session.md). Chrome's retractable bars permanently cost
 * the game board height on phones (the board never scrolls, and scroll is the only
 * other thing that hides them — measured +109px/+45% cell height in fullscreen on
 * Andre's Pixel 5, landscape). Root-homed (js/CLAUDE.md's `game-handle.js` precedent) —
 * two consumers in different packages now, not one screen's private concern.
 */
import { smallViewportHeight } from "./viewport-settle.js";

// why: 100lvh (chrome retracted) vs. 100svh (chrome shown) tests the causal quantity —
// "does this browser have retractable chrome actually costing height" — rather than
// proxying it through input type (`pointer: coarse`). Desktop has no retractable chrome,
// so lvh === svh there and this self-excludes it: auto-fullscreening a desktop Play click
// would be hostile, and this gate is what prevents that. The svh half of the read is
// shared knowledge with game-start-layout.js's own vh source (viewport-settle.js#
// smallViewportHeight, start-screen-layout-improvements part C) — extracted there once a
// second consumer needed the identical probe; the lvh half stays local, this function's
// own concern, so this runs two insert/measure/remove cycles per call instead of one
// (accepted trade — this fires at button-render frequency, not a hot loop).
function hasRetractableChrome() {
  const probe = document.createElement("div");
  probe.style.position = "fixed";
  probe.style.visibility = "hidden";
  probe.style.pointerEvents = "none";
  probe.style.height = "100lvh";
  document.body.appendChild(probe);
  const lvh = probe.getBoundingClientRect().height;
  probe.remove();
  return lvh > smallViewportHeight();
}

/**
 * Whether requesting fullscreen here can actually gain the viewer board height — the
 * shared visibility gate for both consumers' triggers (the Play button's own gate, and
 * fullscreen-button.js's mount predicate). Re-evaluate live, never cache: `hasRetractableChrome`
 * reads `lvh > svh`, which collapses to `lvh === svh` while already in fullscreen, so a value
 * cached from inside fullscreen would latch `false` forever.
 */
export function canGainHeight() {
  return document.fullscreenEnabled && hasRetractableChrome(); // also excludes iPhone Safari, which lacks the API
}

/**
 * Request fullscreen. Must be called synchronously, at the top of the click handler —
 * requestFullscreen() only honors a trusted user gesture, and any awaited work ahead of it
 * (or a wrapping async function that yields first) would forfeit it.
 */
export function enterFullscreen() {
  if (!canGainHeight()) return;

  // Fullscreen is a pure enhancement, not a game requirement: a denied (permissions
  // policy), unsupported, or interrupted request must never break Play. Legitimate
  // catch-to-recover, not a defensive swallow (CODING.md "let errors propagate") —
  // recovery is simply playing on at the bar-shown viewport. Warn rather than
  // swallow silently, per js/CLAUDE.md § Error handling: a permissions-policy block
  // or a gate regression would otherwise leave nothing to notice it by.
  document.documentElement.requestFullscreen().catch((e) => {
    console.warn("[fullscreen]", e.message);
  });
}
